<template>
  <div class="home-dashboard" v-if="userType !== '4'">
    <HomeQuickActions :has-pdv="hasPdv" />

    <el-row :gutter="12">
      <el-col :lg="16" :md="24" :sm="24" :xs="24">
        <el-card shadow="never" class="home-dashboard__card" v-loading="loading">
          <div slot="header" class="home-dashboard__head">
            <span class="home-dashboard__card-title">
              {{ isOnboarding ? $t("primeiros-passos") : $t("saude-postagens") }}
              <small v-if="!isOnboarding && dashboard.counters.total > 0" class="home-dashboard__card-count">
                ({{ dashboard.counters.total.toLocaleString("pt-BR") }} registros)
              </small>
            </span>
            <PeriodSwitch v-if="!isOnboarding" v-model="days" :options="dayOptions" @change="fetchDashboard" />
          </div>

          <HomeOnboarding v-if="isOnboarding" />

          <div v-else-if="dashboard.counters.total > 0" class="home-dashboard__tiles" style="border-right: none;">
            <div class="home-dashboard__tile home-dashboard__tile--link home-dashboard__tile--primary"
              :title="`${$t('postagens-sem-ocorrencias')} — clique para abrir a listagem`" @click="goAllListing()"
              style="border: 1px solid #f6921e; border-radius: 5px;">
              <span class="home-dashboard__tile-value home-dashboard__tile-value--primary">{{ successRate }}%</span>
              <span class="home-dashboard__tile-label" >{{ $t("postagens-sem-ocorrencias") }}</span>
            </div>
            <div v-for="group in tileGroups" :key="group.key" class="home-dashboard__tile home-dashboard__tile--link"
              :title="`${group.label} — clique para abrir a listagem`" @click="goListing(group.statuses)">
              <span class="home-dashboard__tile-value">{{ groupCounts[group.key].toLocaleString("pt-BR") }}</span>
              <span class="home-dashboard__tile-label">
                <i :class="group.icon" :style="{ color: group.color }"></i> {{ group.label }}
              </span>
            </div>
          </div>
          <div v-else-if="!loading" class="home-dashboard__empty">
            {{ $t("sem-postagens-30-dias") }}
          </div>
        </el-card>
        <HomeFreightCalc v-if="!isOnboarding" :user-type="userType" />

        <HomeStateMap v-if="!isOnboarding"/>

        <HomeProfitChart v-if="isManager && !isOnboarding" />
        
        <!-- <HomeSlaChart v-if="!isOnboarding" /> -->
      </el-col>

      <el-col :lg="8" :md="24" :sm="24" :xs="24">
        <el-card v-if="!isOnboarding" shadow="never" class="home-dashboard__card" v-loading="pendingLoading">
          <div slot="header" class="home-dashboard__head">
            <span class="home-dashboard__card-title">{{ $t("pendencias") }}</span>
            <PeriodSwitch v-model="pendingDays" :options="dayOptions" @change="fetchPending" />
          </div>
          <HomePendingList :pending="pendingInfo.pending" :period="pendingInfo.period" />
        </el-card>
        <HomeShortcuts :menu-items="menuItems" />

        <HomeStateDonut v-if="!isOnboarding" />

        <HomeNotices />
      </el-col>
    </el-row>

    <AcceptTermsModal
      v-if="showUserTermsModal"
      :termsUrl="termsUrl"
      :showModal="showUserTermsModal"
      @update:showModal="showUserTermsModal = $event"
    />
  </div>
</template>

<script>
import AcceptTermsModal from "@/views/AcceptTermsModal";
import { UserService } from "@/services/users/user.services";
import { TokenService } from "@/services/auth/storage.services";
import { PrepostCountService } from "@/services/customers/prepost.count.services";
import { STATUS_GROUPS } from "@/xpost/helpers/prepost-status";
import PeriodSwitch, { DAY_OPTIONS } from "./PeriodSwitch.vue";
import HomeQuickActions from "./HomeQuickActions.vue";
import HomePendingList from "./HomePendingList.vue";
import HomeShortcuts from "./HomeShortcuts.vue";
import HomeNotices from "./HomeNotices.vue";
import HomeOnboarding from "./HomeOnboarding.vue";
import HomeFreightCalc from "./HomeFreightCalc.vue";
import HomeStateMap from "./HomeStateMap.vue";
import HomeStateDonut from "./HomeStateDonut.vue";
// import HomeSlaChart from "./HomeSlaChart.vue";
import HomeProfitChart from "./HomeProfitChart.vue";

export default {
  name: "HomeDashboard",
  components: {
    PeriodSwitch,
    HomeQuickActions,
    HomePendingList,
    HomeShortcuts,
    HomeNotices,
    HomeOnboarding,
    HomeFreightCalc,
    HomeStateMap,
    HomeStateDonut,
    // HomeSlaChart,
    HomeProfitChart,
    AcceptTermsModal,
  },
  data() {
    return {
      userType: "4",
      showUserTermsModal: false,
      termsUrl: undefined,
      loading: false,
      loaded: false,
      days: 30,
      dayOptions: DAY_OPTIONS,
      pendingDays: 30,
      pendingLoading: false,
      pendingInfo: {
        period: {},
        pending: { expiredLabels: 0, deliveryFailures: 0, devolutions: 0 },
      },
      menuItems: [],
      dashboard: {
        period: {},
        counters: {
          delivered: 0,
          inTransit: 0,
          devolution: 0,
          awaitingWithdrawal: 0,
          lost: 0,
          deliveryIssue: 0,
          cancelled: 0,
          total: 0,
        },
        byStatus: [],
        total90d: 0,
      },
    };
  },
  computed: {
    tileGroups() {
      const order = ["delivered", "progress", "pre", "attention"];
      return order.map((key) => STATUS_GROUPS.find((group) => group.key === key));
    },
    groupCounts() {
      const byStatus = this.dashboard.byStatus || [];
      const counts = {};
      STATUS_GROUPS.forEach((group) => {
        counts[group.key] = byStatus
          .filter((row) => group.statuses.includes(String(row.status)))
          .reduce((acc, row) => acc + +row.quantity, 0);
      });
      return counts;
    },
    successRate() {
      const total = this.dashboard.counters.total;
      if (!total) {
        return 100;
      }
      return +(((total - this.groupCounts.attention) / total) * 100).toFixed(1);
    },
    isOnboarding() {
      if (!this.loaded) {
        return false;
      }
      return this.dashboard.counters.total === 0 && this.dashboard.total90d === 0;
    },
    isManager() {
      return this.userType === "1" || this.userType === "3";
    },
    hasPdv() {
      return this.menuItems.some((item) => item.father === "licence");
    },
  },
  methods: {
    goAllListing() {
      const period = this.dashboard.period || {};
      this.$router.push({
        name: "post",
        query: {
          tab: "all",
          byCreationDate: "1",
          startedAt: period.startedAt,
          finishedAt: period.finishedAt,
        },
      });
    },
    goListing(statuses) {
      const period = this.dashboard.period || {};
      this.$router.push({
        name: "post",
        query: {
          statuses,
          byCreationDate: "1",
          startedAt: period.startedAt,
          finishedAt: period.finishedAt,
        },
      });
    },
    fetchDashboard() {
      const vm = this;
      vm.loading = true;
      PrepostCountService.dashboardHome(vm.days)
        .then((response) => {
          vm.dashboard = { ...vm.dashboard, ...response };
          vm.loaded = true;
        })
        .catch((error) => {
          vm.$message({ type: "error", message: error });
        })
        .finally(() => {
          vm.loading = false;
        });
    },
    // Pendências têm período próprio: mesmo endpoint, janela independente da Saúde
    fetchPending() {
      const vm = this;
      vm.pendingLoading = true;
      PrepostCountService.dashboardHome(vm.pendingDays)
        .then((response) => {
          vm.pendingInfo = {
            period: response.period || {},
            pending: { ...vm.pendingInfo.pending, ...(response.pending || {}) },
          };
        })
        .catch((error) => {
          vm.$message({ type: "error", message: error });
        })
        .finally(() => {
          vm.pendingLoading = false;
        });
    },
    fetchMenu() {
      const vm = this;
      UserService.menu()
        .then((response) => {
          vm.menuItems = (response.user && response.user.menu) || [];
        })
        .catch(() => {
          vm.menuItems = []; // sem menu, os blocos modulares apenas não aparecem
        });
    },
  },
  mounted() {
    const vm = this;
    UserService.me()
      .then((response) => {
        const { type, termsAccepted } = response;
        vm.userType = type;
        // Na conta do cliente via ENTRAR o aceite é do próprio cliente, não do admin.
        vm.showUserTermsModal = !termsAccepted && !TokenService.isImpersonating();
        vm.termsUrl = "termo-de-uso.pdf";
        if (type !== "4") {
          vm.fetchDashboard();
          vm.fetchPending();
          vm.fetchMenu();
        }
      })
      .catch((error) => {
        vm.$message({
          type: "error",
          message: error,
        });
      });
  },
};
</script>

<style>
.home-dashboard__card {
  margin-bottom: 10px;
  animation: home-fade-up 0.35s ease both;
}

.home-dashboard__card:nth-of-type(2) {
  animation-delay: 0.06s;
}

.home-dashboard__card:nth-of-type(3) {
  animation-delay: 0.12s;
}

@keyframes home-fade-up {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .home-dashboard__card,
  .home-quick-actions__card,
  .home-dashboard__tile {
    animation: none !important;
    transition: none !important;
  }
}

.home-dashboard__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.home-dashboard__card-title {
  font-weight: 600;
  color: var(--me-text-primary);
}

.home-dashboard__card-subtitle {
  color: var(--me-text-secondary);
  margin-left: 6px;
}

.home-dashboard__tiles {
  display: flex;
  flex-wrap: wrap;
  padding: 0 !important;
}

.home-dashboard__tile {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  padding: 4px 24px;
  margin-bottom: 8px;
  border-left: 1px solid var(--me-border-lighter);
}

.home-dashboard__tile:first-child {
  border-left: none;
  padding-left: 2px;
}

.home-dashboard__tile--link {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: 1px solid transparent;
}

.home-dashboard__tile--link:hover {
  border: 1px solid #e5e7eb; border-radius: 5px;
}

.home-dashboard__tile--link:hover .home-dashboard__tile-value {
  color: #f6921e;
}

.home-dashboard__tile-value {
  font-size: 22px;
  font-weight: 700;
  color: #22418d;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  transition: color 0.15s ease;
}

.home-dashboard__tile--primary {
  background: rgba(246, 146, 30, 0.08);
  padding-left: 14px!important;
}
.home-dashboard__tile--primary:hover {
  border: 1px solid #f6911e66!important; 
}
.home-dashboard__tile-value--primary {
  color: #f6921e;
}

.home-health .el-card__header {
  border-bottom: 2px solid #22418d;
  border-image: linear-gradient(90deg, #22418d, #f6921e) 1;
}

.home-dashboard__tile-label {
  font-size: 12px;
  color: var(--me-text-secondary);
  margin-top: 2px;
  white-space: nowrap;
}

.home-dashboard__tile-label i {
  font-size: 13px;
  margin-right: 4px;
}

.home-dashboard__empty {
  color: var(--me-text-secondary);
  text-align: center;
  padding: 20px 0;
}
.home-dashboard__card-count {
  font-size: 12px;
  font-weight: 400;
  color: var(--me-text-secondary);
  margin-left: 2px;
}
</style>
